import type { RetryAttempt, RetryStatus } from '@/api/retry-processor/mock';
import { cn } from '@/lib/utils';

// Single-select status filter chips — replace the retired colored stat tiles.
// Accent vocabulary reuses the old stat-tile ACCENT_BY_ID mapping (info /
// success / danger / success) so the color semantics carry over unchanged.
type Accent = 'success' | 'warning' | 'danger' | 'info';

type Category = {
	id: string;
	label: string;
	statuses: RetryStatus[];
	accent: Accent;
	isRate?: boolean;
};

export const RETRY_FILTER_CATEGORIES: readonly Category[] = [
	{ id: 'in-queue', label: 'In Retry Queue', statuses: ['retrying', 'pending'], accent: 'info' },
	{ id: 'recovered', label: 'Recovered', statuses: ['succeeded'], accent: 'success' },
	{ id: 'exhausted', label: 'Exhausted', statuses: ['exhausted'], accent: 'danger' },
	{
		id: 'resolved',
		label: 'Resolved',
		statuses: ['succeeded', 'exhausted'],
		accent: 'success',
		isRate: true,
	},
];

/** Resolve an active chip id to the status set AttemptsTable filters on. */
export function statusesForCategory(id: string | null): RetryStatus[] | null {
	if (!id) return null;
	return RETRY_FILTER_CATEGORIES.find((c) => c.id === id)?.statuses ?? null;
}

// Active-state chip styling — accent border + tint + text, keyed by semantics.
const ACCENT_ACTIVE: Record<Accent, string> = {
	success: 'border-success bg-success/10 text-success',
	danger: 'border-danger bg-danger/10 text-danger',
	warning: 'border-warning bg-warning/10 text-warning',
	info: 'border-info bg-info/10 text-info',
};

function recoveryRate(attempts: RetryAttempt[]): string {
	const succeeded = attempts.filter((a) => a.status === 'succeeded').length;
	const exhausted = attempts.filter((a) => a.status === 'exhausted').length;
	const denom = succeeded + exhausted;
	if (denom === 0) return '—';
	return `${((succeeded / denom) * 100).toFixed(1)}%`;
}

export function StatusFilterChips({
	attempts,
	active,
	onSelect,
}: {
	attempts: RetryAttempt[];
	active: string | null;
	onSelect: (id: string | null) => void;
}) {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
			{RETRY_FILTER_CATEGORIES.map((cat) => {
				const isActive = active === cat.id;
				const value = cat.isRate
					? recoveryRate(attempts)
					: attempts
							.filter((a) => cat.statuses.includes(a.status))
							.length.toLocaleString('en-US');
				return (
					<button
						key={cat.id}
						type="button"
						onClick={() => onSelect(isActive ? null : cat.id)}
						aria-pressed={isActive}
						className={cn(
							'flex flex-col items-start gap-1 rounded-md border p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
							isActive
								? ACCENT_ACTIVE[cat.accent]
								: 'border-border bg-card text-muted-foreground hover:bg-accent',
						)}
					>
						<span className="font-mono text-xs uppercase tracking-wide">
							{cat.label}
						</span>
						<span className="font-serif text-3xl font-bold tabular-nums text-foreground">
							{value}
						</span>
					</button>
				);
			})}
		</div>
	);
}
